import { trackEvent } from "@/lib/analytics";
import type { MarketingPageKey } from "@/services/marketing-analytics";

const ATTRIBUTION_STORAGE_KEY = "imarketer.marketingAttribution";

type MarketingTrackingBase = {
  pageKey: MarketingPageKey;
  variantId?: string;
  experimentId?: string;
};

type MarketingAttribution = {
  page_key: MarketingPageKey;
  variant_id: string;
  experiment_id?: string;
  path: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  first_seen_at: string;
  latest_seen_at: string;
};

function getCurrentUrlProperties() {
  if (typeof window === "undefined") return {};

  const url = new URL(window.location.href);
  return {
    path: `${url.pathname}${url.search}`,
    referrer: document.referrer || undefined,
    utm_source: url.searchParams.get("utm_source") || undefined,
    utm_medium: url.searchParams.get("utm_medium") || undefined,
    utm_campaign: url.searchParams.get("utm_campaign") || undefined,
  };
}

// Keep attribution client-side so signup events can be tied back to the page/variant that drove them.
function readMarketingAttribution(): MarketingAttribution | null {
  if (typeof window === "undefined") return null;

  try {
    const value = window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY);
    return value ? (JSON.parse(value) as MarketingAttribution) : null;
  } catch {
    return null;
  }
}

function writeMarketingAttribution(attribution: MarketingAttribution) {
  if (typeof window === "undefined") return;

  try {
    window.localStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch {
    // Ignore storage failures; analytics capture can still proceed without persisted attribution.
  }
}

// Signup events reuse the latest marketing touchpoint without each auth page needing to know page details.
function getMarketingAttributionProperties() {
  const attribution = readMarketingAttribution();
  if (!attribution) return {};

  return {
    page_key: attribution.page_key,
    variant_id: attribution.variant_id,
    experiment_id: attribution.experiment_id,
    marketing_path: attribution.path,
    marketing_referrer: attribution.referrer,
    utm_source: attribution.utm_source,
    utm_medium: attribution.utm_medium,
    utm_campaign: attribution.utm_campaign,
    marketing_first_seen_at: attribution.first_seen_at,
    marketing_latest_seen_at: attribution.latest_seen_at,
  };
}

export function trackMarketingPageViewed({
  pageKey,
  variantId = "control",
  experimentId,
}: MarketingTrackingBase) {
  const now = new Date().toISOString();
  const current = getCurrentUrlProperties();
  const previous = readMarketingAttribution();
  // Preserve the original first touch while updating the latest touch for rolling attribution.
  const attribution: MarketingAttribution = {
    page_key: pageKey,
    variant_id: variantId,
    experiment_id: experimentId,
    path: current.path ?? "",
    referrer: current.referrer,
    utm_source: current.utm_source,
    utm_medium: current.utm_medium,
    utm_campaign: current.utm_campaign,
    first_seen_at: previous?.first_seen_at ?? now,
    latest_seen_at: now,
  };

  writeMarketingAttribution(attribution);
  trackEvent("marketing_page_viewed", {
    page_key: pageKey,
    variant_id: variantId,
    experiment_id: experimentId,
    ...current,
  });
}

export function trackMarketingCtaClicked({
  pageKey,
  variantId = "control",
  experimentId,
  ctaSlotId,
  ctaText,
}: MarketingTrackingBase & {
  ctaSlotId: string;
  ctaText: string;
}) {
  // CTA slots let IMarketer compare button locations/copy without relying on DOM text scraping.
  trackEvent("marketing_cta_clicked", {
    page_key: pageKey,
    variant_id: variantId,
    experiment_id: experimentId,
    cta_slot_id: ctaSlotId,
    cta_text: ctaText,
    ...getCurrentUrlProperties(),
  });
}

export function trackSignupStarted(method: string) {
  trackEvent("signup_started", {
    method,
    ...getMarketingAttributionProperties(),
  });
}

export function trackSignupCompleted(method: string, userId?: string) {
  trackEvent("signup_completed", {
    method,
    user_id: userId,
    ...getMarketingAttributionProperties(),
  });
}
